import { useEffect, useState } from "react";
import axios from "../../config/axios";
import baseURL from "../../config/api";

type VideoPlayerProps = {
    videoId: number | null;
    onClose?: () => void;
};

const getYouTubeId = (url: string) => {
    try {
        if (url.includes("youtu.be")) {
            return url.split("youtu.be/")[1].split("?")[0];
        }
        if (url.includes("watch?v=")) {
            return new URL(url).searchParams.get("v");
        }
        if (url.includes("embed/")) {
            return url.split("embed/")[1].split("?")[0];
        }
        return null;
    } catch {
        return null;
    }
};

const getEmbedUrl = (url: string) => {
    const id = getYouTubeId(url);
    return id ? `https://www.youtube.com/embed/${id}?autoplay=1` : url;
};

const getThumbnailUrl = (url: string) => {
    const id = getYouTubeId(url);
    return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : "";
};

export const VideoPlayer = ({ videoId, onClose }: VideoPlayerProps) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [videoLoaded, setVideoLoaded] = useState(false);
    const [video, setVideo] = useState<any>({});

    useEffect(() => {
        if (videoId) {
            setVideoLoaded(false);
            axios.get(`${baseURL}/videos/${videoId}`)
                .then((response) => {
                    setVideo(response.data)
                    setVideoLoaded(true);
                })
                .catch((error) => console.error(error))
        }
    }, [videoId])

    return (
        <div className="bg-white p-4 rounded-md shadow-md">
            {
                (videoLoaded && video) && (
                    <>
                        <div className="flex justify-between items-center mb-3">
                            <h2 className="text-lg font-semibold">{video.title}</h2>
                            <button
                                onClick={onClose}
                                className="text-sm px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600"
                            >
                                Close
                            </button>
                        </div>

                        <div className="w-full h-96 mb-3 relative">
                            {!isPlaying ? (
                                <div
                                    className="w-full h-full relative cursor-pointer group"
                                    onClick={() => setIsPlaying(true)}
                                >
                                    <img
                                        src={getThumbnailUrl(video.videoUrl)}
                                        alt={video.title}
                                        className="w-full h-full object-cover rounded"
                                    />

                                    <div className="absolute inset-0 flex items-center justify-center">
                                        <div className="bg-primary rounded-full p-4 transition shadow-lg">
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                className="h-12 w-12 text-white"
                                                fill="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path d="M8 5v14l11-7z" />
                                            </svg>
                                        </div>
                                    </div>

                                </div>
                            ) : (
                                <iframe
                                    src={getEmbedUrl(video.videoUrl)}
                                    title={video.title}
                                    className="w-full h-full rounded"
                                    allow="autoplay; encrypted-media"
                                    allowFullScreen
                                />
                            )}
                        </div>
                    </>
                )
            }

            {
                !videoLoaded && (
                    <div className="flex items-center justify-center h-64">
                        <div className="flex flex-col items-center gap-3">
                            <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                            <p className="text-gray-600 font-medium">Loading...</p>
                        </div>
                    </div>
                )
            }
        </div>
    );
};